import React from 'react'
import { useSearchParams } from 'react-router'

/** Enables proper browser back/forward navigation by preserving history.
 *
 *  Usage requires supplied generic for maximum type safety:
 *   ie. `useSearchParamState<StringLiteralUnion>(..)` */
export const useSearchParamState = <T extends string>(state: {
  key: string
  values: T[]
  defaultValue: T
}): [T, (newValue: T, opts?: SetSearchParamsOptions) => void] => {
  const { key, values, defaultValue } = state

  const [searchParams, setSearchParams] = useSearchParams({ [key]: defaultValue })

  const currentValue = searchParams.get(key) as (typeof values)[0]
  const isValidValue = values.includes(currentValue)
  const value = isValidValue ? currentValue : defaultValue

  // Reset URL to default if value is not recognised
  React.useEffect(() => {
    if (currentValue !== value) {
      setSearchParams(
        prevParams => {
          const newParams = new URLSearchParams(prevParams)
          newParams.set(key, value)
          return newParams
        },
        { replace: true }
      )
    }
  }, [key, setSearchParams, currentValue, value])

  const setValue = React.useCallback(
    (value: (typeof values)[0], opts: SetSearchParamsOptions = {}) => {
      setSearchParams(
        prevParams => {
          const newParams = new URLSearchParams(prevParams)
          opts.paramsToDelete?.forEach(paramToDelete => {
            newParams.delete(paramToDelete)
          })
          newParams.set(key, value)
          return newParams
        },
        { replace: !!opts.shouldReplace }
      )
    },
    [setSearchParams, key]
  )

  return [value, setValue]
}

// =====
// Types
// =====

type SetSearchParamsOptions = {
  shouldReplace?: boolean
  paramsToDelete?: string[]
}
